import { useCallback, useMemo } from 'react';
import {
    cleanNumericInput,
    toMonthlyIncome,
    toAnnualIncome,
    currencyOptions,
    MAX_MONTHLY_INCOME,
    MAX_ANNUAL_INCOME,
} from '../../../utils/income-utils';
import { generateErrorMessage } from '../../../utils/error-message';

const useMonthlyIncomeHandling = () => {
    const errorMessages = useMemo(
        () =>
            ({
                VAL_TOO_SMALL: {
                    monthly: 'Monthly Income must be at least 3 digits',
                    yearly: 'Yearly Income must be at least $1200',
                },
                EXCEEDS_MIN: {
                    monthly: 'Monthly Income must be at least 3 digits',
                    yearly: 'Yearly Income must be at least $1200',
                },
                EXCEEDS_MAX: {
                    monthly:
                        'Monthly Income exceeds amount allowed in this field',
                    yearly: 'Yearly Income exceeds amount allowed in this field',
                },
                MISSING_VALUE: {
                    monthly: generateErrorMessage('Total Monthly Income', 'missingValue'),
                    yearly: generateErrorMessage('Total Yearly Income', 'missingValue'),
                },
                DEFAULT: { 
                    monthly: generateErrorMessage('Total Monthly Income'),
                    yearly: generateErrorMessage('Total Yearly Income'),
                },
            } as const),
        [],
    );

    const incomeLimits = useMemo(
        () =>
            ({
                lengthRequirement: {
                    monthly: 3,
                    yearly: 4,
                },
                min: {
                    monthly: 100,
                    yearly: 1200,
                },
                max: {
                    monthly: MAX_MONTHLY_INCOME,
                    yearly: MAX_ANNUAL_INCOME,
                },
                popupThreshold: {
                    monthly: 500,
                    yearly: 6000,
                },
            } as const),
        [],
    );

    const convertValue = useCallback(
        (
            valueToConvert: string | number,
            currentRadioValue: 'yearly' | 'monthly',
        ): string => {
            if (typeof valueToConvert === 'string') {
                valueToConvert = Number(cleanNumericInput(valueToConvert));
            }
            const toReturn =
                currentRadioValue === 'yearly'
                    ? toMonthlyIncome(valueToConvert)
                    : toAnnualIncome(valueToConvert);
            return toReturn > 0
                ? toReturn.toLocaleString('en-US', currencyOptions)
                : '';
        },
        [cleanNumericInput, toMonthlyIncome, toAnnualIncome],
    );

    return {
        incomeLimits,
        convertValue,
        errorMessages,
    };
};

export default useMonthlyIncomeHandling;
